import { useEffect, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  Briefcase,
  Users,
  Star,
  Zap,
  Columns,
  PieChart,
  CheckCircle,
  ArrowRight,
} from 'lucide-react'
import { LandingHeader } from '../components/landing/LandingHeader'
import { LandingFooter } from '../components/landing/LandingFooter'

gsap.registerPlugin(ScrollTrigger)

export const Route = createFileRoute('/rekrut-product')({
  component: RekrutProductPage,
})

const features = [
  {
    Icon: Briefcase,
    title: 'Job Posting Management',
    description: "Create job openings with required competency profiles. Define the skills and experience levels you need — Rekrut uses them to score every candidate automatically.",
  },
  {
    Icon: Users,
    title: 'Candidate Database',
    description: 'Maintain a rich candidate database with profiles, application history, and competency scores. Filter and search across all candidates instantly.',
  },
  {
    Icon: Star,
    title: 'CV Competency Extraction',
    description: 'Upload candidate CV text and let AI extract and score competencies automatically. No manual screening required — candidates are ranked the moment they apply.',
  },
  {
    Icon: Zap,
    title: 'AI Match Scoring',
    description: "Every candidate receives an automatic match score against the job's required competencies. Sort your pipeline by fit score and focus on the top candidates first.",
  },
  {
    Icon: Columns,
    title: 'Application Pipeline',
    description: 'Move candidates through hiring stages — New, Screening, Interview, Offer, Hired — with full status tracking and history.',
  },
  {
    Icon: PieChart,
    title: 'Recruitment Analytics',
    description: 'Track pipeline health, application volume, and hiring outcomes. Understand which roles are hardest to fill and where candidates drop off.',
  },
]

const benefits = [
  'Surface the best-fit candidates instantly with AI match scoring',
  'Eliminate bias with competency-based evaluation criteria',
  'Keep every hiring stakeholder aligned with shared pipeline views',
  'Scale from 1 to unlimited open roles without complexity',
]

// ─── Hero Mockup ───────────────────────────────────────────────────────────────

function RekrutHeroMockup() {
  const candidates = [
    { initials: 'SC', name: 'Sarah Chen', role: 'Sr. Engineer', match: 94, status: 'Interviewed', statusColor: '#359d9e' },
    { initials: 'BS', name: 'Budi Santoso', role: 'Engineer II', match: 87, status: 'Screening', statusColor: '#368fc1' },
    { initials: 'RP', name: 'Rina Putri', role: 'Engineer I', match: 79, status: 'New', statusColor: '#b3b7b7' },
  ]

  return (
    <div className="relative w-full h-full select-none">
      {/* Main panel */}
      <div className="absolute rounded-2xl overflow-hidden shadow-[0px_8px_40px_0px_rgba(53,157,158,0.18)] bg-white border border-[rgba(53,157,158,0.12)]"
        style={{ top: '8%', left: '0%', right: '8%', bottom: '6%' }}>
        <div className="flex items-center gap-1.5 px-3 py-2 bg-[#f8fffe] border-b border-[rgba(53,157,158,0.1)]">
          <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <div className="flex-1 mx-3 h-4 rounded bg-[#e6e7e7] text-[9px] text-[#b3b7b7] flex items-center px-2">pelajari.ai/rekrut</div>
        </div>
        <div className="p-4 flex flex-col gap-3">
          {/* Job card */}
          <div className="bg-[#f2fbfa] border border-[#d5f2ef] rounded-lg p-2.5 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-white border border-[#d5f2ef] flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 12 12" className="w-3.5 h-3.5" fill="none">
                <rect x="1.5" y="3" width="9" height="7.5" rx="1" stroke="#359d9e" strokeWidth="1.1"/>
                <path d="M4 3V2.5C4 1.67 4.67 1 5.5 1h1C7.33 1 8 1.67 8 2.5V3" stroke="#359d9e" strokeWidth="1.1"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-[#1a2727] truncate">Senior Software Engineer</p>
              <p className="text-[8px] text-[#b3b7b7]">Engineering · 12 applicants</p>
            </div>
            <div className="bg-[#359d9e] text-white text-[7px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0">Open</div>
          </div>

          {/* Ranked candidates */}
          <p className="text-[8px] font-bold text-[#b3b7b7] uppercase tracking-wider">AI-Ranked Candidates</p>
          <div className="flex flex-col gap-2">
            {candidates.map((c, i) => (
              <div key={c.initials} className="flex items-center gap-2.5">
                <span className="text-[8px] font-bold text-[#b3b7b7] w-3 flex-shrink-0">#{i + 1}</span>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: c.statusColor }}>
                  {c.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-semibold text-[#1a2727] truncate">{c.name}</p>
                  <p className="text-[8px] text-[#b3b7b7] truncate">{c.role}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[9px] font-bold" style={{ color: c.statusColor }}>{c.match}%</span>
                  <span className="text-[7px] font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: `${c.statusColor}18`, color: c.statusColor }}>
                    {c.status}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Match bar for #1 */}
          <div className="bg-[#f8fffe] rounded-lg p-2 flex flex-col gap-1">
            <div className="flex justify-between">
              <span className="text-[8px] text-[#1a2727]">Competency match · Sarah Chen</span>
              <span className="text-[8px] font-bold text-[#359d9e]">94%</span>
            </div>
            <div className="h-1.5 bg-[#e6e7e7] rounded-full overflow-hidden">
              <div className="h-1.5 bg-[#359d9e] rounded-full" style={{ width: '94%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Floating card */}
      <div className="rekrut-float-card absolute bg-white rounded-xl border border-[rgba(53,157,158,0.15)] shadow-[0px_4px_20px_0px_rgba(53,157,158,0.2)] px-3 py-2.5 flex items-center gap-2.5"
        style={{ bottom: '0%', right: '0%', maxWidth: '55%' }}>
        <div className="w-6 h-6 rounded-full bg-[#f2fbfa] flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 12 12" className="w-3.5 h-3.5" fill="none">
            <path d="M6 1L7.5 4.5H11L8 6.5L9 10L6 8L3 10L4 6.5L1 4.5H4.5L6 1Z" fill="#359d9e"/>
          </svg>
        </div>
        <div>
          <p className="text-[9px] font-bold text-[#1a2727]">CV analyzed in 2s</p>
          <p className="text-[8px] text-[#b3b7b7]">94% match detected</p>
        </div>
      </div>
    </div>
  )
}

function RekrutProductPage() {
  const pageRef = useRef<HTMLDivElement>(null)

  // ── Hero refs
  const heroBadgeRef = useRef<HTMLSpanElement>(null)
  const heroHeadingRef = useRef<HTMLHeadingElement>(null)
  const heroSubRef = useRef<HTMLParagraphElement>(null)
  const heroCtaRef = useRef<HTMLDivElement>(null)
  const heroMockupRef = useRef<HTMLDivElement>(null)

  // ── Features refs
  const featHeadingRef = useRef<HTMLDivElement>(null)
  const featCardRefs = useRef<(HTMLDivElement | null)[]>([])

  // ── Benefits refs
  const benefitsLeftRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Hero entrance timeline
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
      gsap.set([heroBadgeRef.current, heroHeadingRef.current, heroSubRef.current, heroCtaRef.current], { opacity: 0, y: 28 })
      gsap.set(heroMockupRef.current, { opacity: 0, x: 48 })

      tl.to(heroBadgeRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'back.out(1.7)' })
        .to(heroHeadingRef.current, { opacity: 1, y: 0, duration: 0.7 }, '-=0.3')
        .to(heroSubRef.current, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4')
        .to(heroCtaRef.current, { opacity: 1, y: 0, duration: 0.55 }, '-=0.35')
        .to(heroMockupRef.current, { opacity: 1, x: 0, duration: 0.85, ease: 'power2.out' }, 0.15)

      // Mockup idle float
      tl.add(() => {
        gsap.to(heroMockupRef.current, { y: -10, duration: 3.2, ease: 'sine.inOut', yoyo: true, repeat: -1 })
        gsap.to('.rekrut-float-card', { y: -7, duration: 2.9, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 0.7 })
      })

      // ── Features section
      gsap.set(featHeadingRef.current, { opacity: 0, y: 24 })
      ScrollTrigger.create({
        trigger: featHeadingRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(featHeadingRef.current, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }),
      })

      gsap.set(featCardRefs.current.filter(Boolean), { opacity: 0, y: 28 })
      ScrollTrigger.create({
        trigger: featCardRefs.current[0],
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(featCardRefs.current.filter(Boolean), {
          opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.08,
        }),
      })

      // ── Benefits + CTA section
      gsap.set(benefitsLeftRef.current, { opacity: 0, y: 28 })
      ScrollTrigger.create({
        trigger: benefitsLeftRef.current,
        start: 'top 82%',
        once: true,
        onEnter: () => gsap.to(benefitsLeftRef.current, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }),
      })

      const statItems = statsRef.current?.querySelectorAll('.stat-item')
      if (statItems?.length) {
        gsap.set(statItems, { opacity: 0, scale: 0.75 })
        ScrollTrigger.create({
          trigger: statsRef.current,
          start: 'top 85%',
          once: true,
          onEnter: () => gsap.to(statItems, {
            opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.7)', stagger: 0.1,
          }),
        })
      }
    }, pageRef)

    return () => ctx.revert()
  }, [])

  return (
    <div ref={pageRef} className="min-h-screen bg-white overflow-x-hidden font-sans">
      <LandingHeader />

      {/* Hero — 2-col split */}
      <section className="relative bg-gradient-to-br from-[#f2fbfa] to-white py-20 px-6 md:px-10 lg:px-[120px] overflow-hidden">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-center lg:gap-16">
            <div className="flex-1 max-w-[560px]">
              <span ref={heroBadgeRef} className="inline-block bg-[#f2fbfa] border border-[#359d9e] text-[#359d9e] text-sm font-medium px-4 py-2 rounded-lg mb-6">
                Rekrut — Smart Recruitment
              </span>
              <h1 ref={heroHeadingRef} className="text-[36px] font-semibold leading-[44px] tracking-[-1px] text-[#000f0f] sm:text-[48px] sm:leading-[58px] lg:text-[56px] lg:leading-[68px] lg:tracking-[-1.5px] mb-6">
                Hire Smarter with{' '}
                <span className="text-[#359d9e]">AI-Powered</span>{' '}
                Recruitment
              </h1>
              <p ref={heroSubRef} className="text-base leading-relaxed text-[#1a2727] mb-8">
                Stop sifting through CVs manually. Rekrut extracts competencies from candidate profiles using AI, scores every applicant against your job requirements, and surfaces the best fits instantly — so your team only talks to the right people.
              </p>
              <div ref={heroCtaRef} className="flex flex-col sm:flex-row gap-4">
                <a href="/login" className="inline-flex items-center justify-center gap-2 bg-[#359d9e] text-white hover:bg-[#27777a] rounded px-6 py-3 font-medium transition-colors">
                  Start Hiring Smarter
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#features" className="inline-flex items-center justify-center gap-2 border border-[#359d9e] text-[#359d9e] hover:bg-[#f2fbfa] rounded px-6 py-3 font-medium transition-colors">
                  See Features
                </a>
              </div>
            </div>
            <div ref={heroMockupRef} className="w-full max-w-[440px] h-[360px] sm:h-[400px] lg:h-[420px] flex-shrink-0">
              <RekrutHeroMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-6 md:px-10 lg:px-[120px] max-w-screen-xl mx-auto">
        <div ref={featHeadingRef} className="text-center mb-14">
          <h2 className="text-[32px] font-semibold leading-[40px] tracking-[-0.5px] text-[#000f0f] sm:text-[40px] sm:leading-[48px] mb-4">
            A complete hiring platform in one place
          </h2>
          <p className="text-base text-[#1a2727] max-w-[600px] mx-auto">
            From job posting to signed offer — Rekrut gives you every tool you need to run a fast, fair, and data-driven recruitment process.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => {
            const { Icon } = feature
            return (
              <div
                key={feature.title}
                ref={(el) => { featCardRefs.current[i] = el }}
                className="bg-white border border-[#d5f2ef] rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-[#f2fbfa] rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#359d9e]" />
                </div>
                <h3 className="text-lg font-semibold text-[#000f0f] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#1a2727] leading-relaxed">{feature.description}</p>
              </div>
            )
          })}
        </div>
      </section>

      {/* Benefits + CTA */}
      <section className="bg-[#359d9e] py-16 px-6 md:px-10 lg:px-[120px]">
        <div className="max-w-screen-xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          <div ref={benefitsLeftRef} className="flex-1 text-center lg:text-left">
            <h2 className="text-[30px] font-semibold leading-[38px] text-white mb-6 lg:text-[36px] lg:leading-[45px]">
              Why hiring teams choose Rekrut
            </h2>
            <div className="flex flex-col gap-4 mb-8">
              {benefits.map((b) => (
                <div key={b} className="flex items-start gap-3 justify-center lg:justify-start">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-base text-white">{b}</span>
                </div>
              ))}
            </div>
            <a
              href="/login"
              className="inline-flex items-center gap-2 bg-white text-[#359d9e] hover:bg-white/90 rounded px-6 py-3 font-medium transition-colors"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div ref={statsRef} className="flex-1 bg-white/10 rounded-xl p-8 max-w-[460px] w-full">
            <div className="grid grid-cols-2 gap-6 text-center">
              {[
                { value: '60%', label: 'Faster Time-to-Hire' },
                { value: 'AI', label: 'Candidate Ranking' },
                { value: '∞', label: 'Open Roles' },
                { value: 'Kanban', label: 'Pipeline View' },
              ].map((stat) => (
                <div key={stat.label} className="stat-item flex flex-col gap-1">
                  <span className="text-[32px] font-semibold text-white">{stat.value}</span>
                  <span className="text-sm text-white/80">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  )
}
